import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const AdminAdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'content_admin' });
  const { success, error } = useToast();

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.data || []);
    } catch { setAdmins([]); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/admins', form);
      success('Admin account created!');
      setShowForm(false);
      setForm({ name: '', email: '', password: '', role: 'content_admin' });
      loadAdmins();
    } catch { error('Failed to create admin.'); }
  };

  const ROLES = {
    super_admin: { bg: '#FFF3E0', color: '#FF6B00', label: 'Super Admin' },
    content_admin: { bg: '#E8F0FB', color: '#1A3C6E', label: 'Content Admin' },
    support_admin: { bg: '#E6F4EC', color: '#1B7F3E', label: 'Support Admin' }
  };

  return (
    <AdminShell
      title="Admin Users"
      subtitle="Manage admin accounts and permissions"
      actions={
        <button onClick={() => setShowForm(true)} style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
          + Add Admin
        </button>
      }
    >
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '16px' }}>

        {/* Role info */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {[
            { role: 'super_admin', desc: 'Full access to everything including delete and settings' },
            { role: 'content_admin', desc: 'Can review and approve content only' },
            { role: 'support_admin', desc: 'Can manage students and support tickets' },
          ].map(r => {
            const badge = ROLES[r.role];
            return (
              <div key={r.role} style={{ padding: '12px', background: badge.bg, borderRadius: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: badge.color, marginBottom: '4px' }}>{badge.label}</div>
                <div style={{ fontSize: '11px', color: '#64748B' }}>{r.desc}</div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Admin', 'Role', 'Last Login', 'Status'].map(h => (
                  <th key={h} style={{ fontSize: '10px', fontWeight: 800, color: '#64748B', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>No admins found</td></tr>
              ) : admins.map(a => {
                const badge = ROLES[a.role] || ROLES.content_admin;
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #F7F8FC' }}>
                    <td style={{ padding: '10px 8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700 }}>{a.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748B' }}>{a.email}</div>
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 800, background: badge.bg, color: badge.color, borderRadius: '20px', padding: '2px 8px' }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '10px 8px', fontSize: '11px', color: '#64748B' }}>
                      {a.last_login_at ? new Date(a.last_login_at).toLocaleString('en-IN') : 'Never'}
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 800, background: a.is_active ? '#E6F4EC' : '#FEE2E2', color: a.is_active ? '#1B7F3E' : '#DC2626', borderRadius: '20px', padding: '2px 8px' }}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create admin modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 800 }}>Create Admin Account</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748B' }}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              {[
                { key: 'name', label: 'Full Name', type: 'text', placeholder: 'Admin name' },
                { key: 'email', label: 'Email', type: 'email', placeholder: 'admin@mypersonalguruji.com' },
                { key: 'password', label: 'Password', type: 'password', placeholder: 'Min 8 characters' },
              ].map(f => (
                <div key={f.key} style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} required
                    style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }} />
                </div>
              ))}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>Role</label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                  style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }}>
                  <option value="content_admin">Content Admin</option>
                  <option value="support_admin">Support Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '11px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Create Admin</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminAdminsPage;
